<template>
  <div class="member-detail">
    <nav class="breadcrumb">
      <router-link class="breadcrumb__link" to="/">{{ breadcrumb.home }}</router-link>
      <span class="breadcrumb__separator">&gt;</span>
      <router-link class="breadcrumb__link" :to="{ name: 'Members' }">
        {{ breadcrumb.list }}
      </router-link>
      <span class="breadcrumb__separator">&gt;</span>
      <span class="breadcrumb__current">{{ breadcrumb.current }}</span>
    </nav>

    <section v-if="profile" class="profile">
      <div class="profile__hero">
        <div class="profile__media">
          <img v-lazy="profileImage" :alt="`${profile.name} portrait`" loading="lazy" />
        </div>

        <div class="profile__intro">
          <h1 class="profile__name">{{ profile.name }}</h1>
          <p v-if="profile.title" class="profile__title">{{ profile.title }}</p>
          <p v-if="profile.summary" class="profile__summary">{{ profile.summary }}</p>
          <div v-if="hasAffiliation" class="profile__affiliation">
            <h2 class="profile__section-heading">{{ labels.affiliation }}</h2>
            <p v-if="affiliationRole">{{ affiliationRole }}</p>
            <p v-if="affiliationInstitution">{{ affiliationInstitution }}</p>
          </div>
        </div>

        <aside v-if="contactItems.length" class="profile__contacts">
          <h2 class="profile__section-heading profile__contacts-heading">
            {{ labels.contacts }}
          </h2>
          <ul class="contact-list">
            <li
              v-for="item in contactItems"
              :key="`${item.label}-${item.url || item.symbol}`"
              class="contact-list__item"
            >
              <a
                v-if="item.url"
                :href="item.url"
                class="contact-list__link"
                target="_blank"
                rel="noopener"
              >
                <span class="contact-list__icon">{{ item.symbol }}</span>
                <span class="contact-list__label">{{ item.label }}</span>
              </a>
              <div v-else class="contact-list__link contact-list__link--disabled">
                <span class="contact-list__icon">{{ item.symbol }}</span>
                <span class="contact-list__label">{{ item.label }}</span>
              </div>
            </li>
          </ul>
        </aside>
      </div>

      <div class="profile__body" v-if="bioParagraphs.length">
        <section  class="profile-section">
          <h2 class="profile-section__title">{{ labels.biography }}</h2>
          <p
            v-for="paragraph in bioParagraphs"
            :key="paragraph"
            class="profile-section__paragraph"
            v-html="paragraph"
          ></p>
        </section>
      </div>

    </section>

    <section v-else class="profile-empty">
      <div class="profile-empty__card">
        <div class="profile-empty__icon" aria-hidden="true">!</div>
        <h1 class="profile-empty__title">{{ fallback.heading }}</h1>
        <p class="profile-empty__message">{{ fallback.message }}</p>
        <div class="profile-empty__actions">
          <router-link
            class="profile-empty__link profile-empty__link--primary"
            :to="{ name: 'Members' }"
          >
            {{ fallback.actions.members }}
          </router-link>
          <router-link class="profile-empty__link" to="/">
            {{ fallback.actions.home }}
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getLocaleBlock } from '@/utils/i18n';

const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1521119989659-a83eee488004?auto=format&fit=crop&w=480&q=80';

export default {
  name: 'MemberDetailView',
  computed: {
    page() {
      return getLocaleBlock(this.$i18n, 'membersPage', {}) || {};
    },
    labels() {
      const defaults = {
        biography: 'Biography',
        affiliation: 'Affiliation',
        contacts: 'Connect',
      };
      return Object.assign(defaults, this.page.profileLabels || {});
    },
    fallback() {
      const defaults = {
        heading: 'Member profile not found',
        message: 'We are preparing this profile. Please check back soon.',
        actions: {
          members: 'Back to Members',
          home: 'Go Home',
        },
      };
      const block = this.page.profileFallback || {};
      const actions = block.actions || {};
      const legacyAction = block.action;
      return {
        heading: block.heading || defaults.heading,
        message: block.message || defaults.message,
        actions: {
          members: actions.members || legacyAction || defaults.actions.members,
          home: actions.home || defaults.actions.home,
        },
      };
    },
    slug() {
      return this.$route.params?.slug || '';
    },
    profileMap() {
      const profiles = this.page.profiles;
      return profiles && typeof profiles === 'object' ? profiles : {};
    },
    profile() {
      return this.profileMap[this.slug] || null;
    },
    profileImage() {
      return this.profile?.image || DEFAULT_IMAGE;
    },
    affiliationRole() {
      return this.profile?.affiliation?.role || '';
    },
    affiliationInstitution() {
      return this.profile?.affiliation?.institution || '';
    },
    hasAffiliation() {
      return Boolean(this.affiliationRole || this.affiliationInstitution);
    },
    bioParagraphs() {
      return Array.isArray(this.profile?.bio) ? this.profile.bio : [];
    },
    interests() {
      return Array.isArray(this.profile?.interests) ? this.profile.interests : [];
    },
    achievements() {
      return Array.isArray(this.profile?.achievements) ? this.profile.achievements : [];
    },
    noteBlocks() {
      return Array.isArray(this.profile?.noteBlocks) ? this.profile.noteBlocks : [];
    },
    contactItems() {
      const contacts = this.profile?.contacts;
      if (!Array.isArray(contacts)) {
        return [];
      }
      return contacts
        .filter((item) => item && item.label)
        .map((item) => ({
          label: item.label,
          url: item.url || null,
          icon: item.icon || '',
          symbol: this.resolveContactSymbol(item),
        }));
    },
    breadcrumb() {
      const base = this.profile?.breadcrumb || {};
      const fallback = this.page.breadcrumb || {};
      return {
        home: base.home || fallback.home || 'Home',
        list: base.list || fallback.current || 'Members',
        current: this.profile?.name || this.slug || fallback.current || 'Members',
      };
    },
  },
  watch: {
    profile: {
      immediate: true,
      handler() {
        this.updateDocumentTitle();
      },
    },
    '$i18n.locale'() {
      this.updateDocumentTitle();
    },
  },
  methods: {
    resolveContactSymbol(item) {
      const key = (item.icon || '').toString().toLowerCase();
      const label = (item.label || '').toString();
      const map = {
        mail: 'EM',
        email: 'EM',
        calendar: 'CA',
        cv: 'CV',
        'file-text': 'CV',
        scholar: 'GS',
        researchgate: 'RG',
        'id-badge': 'ID',
        linkedin: 'IN',
        github: 'GH',
        twitter: 'TW',
        wechat: 'WE',
        bookmark: 'CN',
      };
      if (map[key]) {
        return map[key];
      }
      const clean = label.trim().replace(/[^A-Za-z0-9\u4e00-\u9fa5]/g, '');
      if (!clean) {
        return '•';
      }
      const slice = clean.slice(0, 2);
      return /[A-Za-z]/.test(slice) ? slice.toUpperCase() : slice;
    },
    updateDocumentTitle() {
      const sectionTitle = this.page?.breadcrumb?.current || 'Members';
      const siteShort = this.$t('meta.siteShort') || '';
      const parts = [];
      if (this.profile?.name) {
        parts.push(this.profile.name);
      }
      parts.push(sectionTitle);
      if (siteShort) {
        parts.push(siteShort);
      }
      const title = parts.filter(Boolean).join(' | ');
      if (title) {
        document.title = title;
      }
    },
  },
};
</script>

<style scoped>
.member-detail {
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 3rem;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  color: #5a6783;
}

.breadcrumb__link {
  color: inherit;
  text-decoration: none;
}

.breadcrumb__link:hover {
  color: #1f4cff;
}

.breadcrumb__separator {
  color: #a3afc7;
}

.breadcrumb__current {
  font-weight: 600;
  color: #1d2e4a;
}

.profile {
  display: flex;
  flex-direction: column;
  gap: 2.6rem;
}

.profile__hero {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 220px;
  gap: 2rem;
  background: #fff;
  border-radius: 28px;
  border: 1px solid rgba(27, 51, 90, 0.08);
  box-shadow: 0 24px 60px rgba(27, 51, 90, 0.14);
  padding: 2.4rem;
}

.profile__media {
  border-radius: 22px;
  overflow: hidden;
  background: #f3f5fb;
}

.profile__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.profile__intro {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.profile__name {
  margin: 0;
  font-size: 2.2rem;
  color: #17233f;
}

.profile__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--primary-color);
}

.profile__summary {
  margin: 0;
  color: #36476a;
  line-height: 1.7;
  overflow-wrap: anywhere;
}

.profile__affiliation {
  margin-top: 1.2rem;
  padding-top: 1.2rem;
  border-top: 1px solid rgba(27, 51, 90, 0.12);
  color: #445679;
  line-height: 1.6;
}

.profile__section-heading {
  font-size: 0.85rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8a96b2;
  margin: 0 0 0.6rem;
}

.profile__contacts {
  align-self: flex-start;
  background: linear-gradient(135deg, rgba(0, 74, 153, 0.12), rgba(0, 74, 153, 0));
  border-radius: 22px;
  border: 1px solid rgba(0, 74, 153, 0.25);
  padding: 1.8rem 1.4rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.profile__contacts-heading {
  color: var(--primary-color);
  letter-spacing: 0.12em;
}

.contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.contact-list__link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.7rem;
  border-radius: 14px;
  text-decoration: none;
  color: var(--primary-color);
  font-weight: 600;
  transition: background-color 0.2s ease, transform 0.2s ease;
}

.contact-list__link:hover {
  background: rgba(0, 74, 153, 0.12);
  transform: translateX(2px);
  color: var(--primary-color-dark);
}

.contact-list__link--disabled {
  cursor: default;
  color: rgba(0, 74, 153, 0.65);
}

.contact-list__icon {
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 50%;
  background: var(--primary-color-light);
  color: var(--primary-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.contact-list__label {
  font-size: 0.95rem;
}

.profile__body {
  background: #fff;
  border-radius: 26px;
  border: 1px solid rgba(27, 51, 90, 0.08);
  box-shadow: 0 24px 60px rgba(27, 51, 90, 0.12);
  padding: 2.4rem;
  display: flex;
  flex-direction: column;
  gap: 2.4rem;
}

.profile-section__title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #12203a;
  margin: 0 0 1.4rem;
}

.profile-section__paragraph {
  margin: 0 0 1.1rem;
  color: #37465f;
  overflow-wrap: anywhere;
  line-height: 1.8;
}

.profile-section__paragraph:last-child {
  margin-bottom: 0;
}

.profile-section__list {
  margin: 0;
  padding-left: 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  color: #354663;
}

.profile-section__list--bullets {
  list-style: disc;
}

.profile-section--notes {
  border-top: 1px solid rgba(27, 51, 90, 0.08);
  padding-top: 1.6rem;
}

.profile-note {
  background: rgba(0, 74, 153, 0.08);
  border-left: 4px solid var(--primary-color);
  border-radius: 16px;
  padding: 1.1rem 1.3rem;
  color: var(--primary-color-dark);
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.profile-note + .profile-note {
  margin-top: 0.85rem;
}

.profile-note__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
}

.profile-note__body {
  margin: 0;
  line-height: 1.7;
}

.profile-empty {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  padding: 0 0.5rem 1rem;
}

.profile-empty__card {
  background: linear-gradient(145deg, rgba(0, 74, 153, 0.05), rgba(0, 74, 153, 0.01)),
    #fff;
  border-radius: 24px;
  border: 1px solid rgba(0, 74, 153, 0.08);
  box-shadow: 0 18px 44px rgba(27, 51, 90, 0.12);
  padding: 2.4rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 620px;
  text-align: left;
}

.profile-empty__icon {
  width: 3rem;
  height: 3rem;
  border-radius: 14px;
  background: rgba(0, 74, 153, 0.12);
  display: grid;
  place-items: center;
  font-size: 1.2rem;
}

.profile-empty__title {
  margin: 0;
  font-size: 1.8rem;
  color: #172941;
}

.profile-empty__message {
  margin: 0;
  color: #4e5d7a;
  line-height: 1.6;
}

.profile-empty__actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.profile-empty__link {
  padding: 0.6rem 1.6rem;
  border-radius: 999px;
  background: #fff;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  border: 1px solid rgba(0, 74, 153, 0.35);
  transition: background-color 0.2s ease, transform 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;
}

.profile-empty__link:hover {
  background: rgba(0, 74, 153, 0.06);
  transform: translateY(-1px);
  box-shadow: 0 12px 26px rgba(0, 74, 153, 0.22);
}

.profile-empty__link--primary {
  background: var(--primary-color);
  color: var(--text-color-on-primary);
  border-color: var(--primary-color);
  box-shadow: 0 12px 26px rgba(0, 74, 153, 0.28);
}

.profile-empty__link--primary:hover {
  background: var(--primary-color-dark);
  box-shadow: 0 14px 30px rgba(0, 51, 102, 0.28);
}

@media (max-width: 1080px) {
  .profile__hero {
    grid-template-columns: 240px minmax(0, 1fr);
  }

  .profile__contacts {
    grid-column: 1 / -1;
    flex-direction: column;
  }
}

@media (max-width: 760px) {
  .profile__hero {
    grid-template-columns: 1fr;
    padding: 1.8rem;
  }

  .profile__media {
    max-width: 260px;
    margin: 0 auto;
  }

  .profile__contacts {
    border: 1px solid rgba(0, 74, 153, 0.2);
  }

  .profile__body {
    padding: 1.8rem;
  }

  .profile-section__title {
    font-size: 1.25rem;
  }
}
</style>
